/**
 * PAX — usePwaUpdateCheck
 *
 * Cienki hook nad istniejącym mechanizmem workbox — wystawia stan ręcznego
 * sprawdzania aktualizacji dla `PwaInstallCard`.
 *
 * NIE tworzy nowego systemu aktualizacji. Nasłuchuje istniejącego eventu
 * `pax:sw-update-available` (emitowanego przez `registerServiceWorker.js`
 * w reakcji na `waiting` workboxa) i wywołuje `checkForServiceWorkerUpdate()`
 * gdy user ręcznie zainicjuje sprawdzenie.
 *
 * API:
 *   - updateStatus: 'idle' | 'checking' | 'available' | 'uptodate' | 'error'
 *   - lastCheckedAt: number (ms epoch) | 0
 *   - checkForUpdates(): Promise<void>
 *   - activate(): void — wywołuje `activateWaitingServiceWorker` (SKIP_WAITING → reload)
 *
 * Automatyczny mechanizm (workbox → PwaUpdateToast) pozostaje BEZ ZMIAN.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  activateWaitingServiceWorker,
  checkForServiceWorkerUpdate,
} from './registerServiceWorker';

const LAST_CHECK_KEY = 'pax:pwa:lastUpdateCheckAt';
// Krótkie okno oczekiwania na event `waiting` po wywołaniu `wb.update()`.
// Workbox emituje `waiting` synchronicznie po odkryciu nowego SW; 1800 ms
// wystarcza w praktyce, a nie blokuje UI zbyt długo.
const WAIT_FOR_WAITING_MS = 1800;

function loadLastCheckedAt() {
  try {
    const raw = localStorage.getItem(LAST_CHECK_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  } catch {
    return 0;
  }
}

export function usePwaUpdateCheck() {
  const [updateStatus, setUpdateStatus] = useState('idle');
  const [lastCheckedAt, setLastCheckedAt] = useState(() => loadLastCheckedAt());
  // Flaga że event `pax:sw-update-available` już się pojawił (kiedykolwiek).
  // Ustawiona → wiemy że nowa wersja czeka i została wykryta automatycznie.
  const updateEventFiredRef = useRef(false);

  useEffect(() => {
    const onUpdate = () => {
      updateEventFiredRef.current = true;
      setUpdateStatus('available');
    };
    window.addEventListener('pax:sw-update-available', onUpdate);
    return () => window.removeEventListener('pax:sw-update-available', onUpdate);
  }, []);

  const checkForUpdates = useCallback(async () => {
    setUpdateStatus('checking');
    // Reset flagi — chcemy wiedzieć czy `waiting` nadejdzie W TEJ próbie.
    // (Jeżeli auto-mechanizm wykrył wcześniej i event już poszedł, `available`
    // nie zniknie — utrzymamy go po sprawdzeniu.)
    const wasAlreadyAvailable = updateEventFiredRef.current;

    const result = await checkForServiceWorkerUpdate();
    const now = Date.now();
    try {
      localStorage.setItem(LAST_CHECK_KEY, String(now));
    } catch {
      /* noop */
    }
    setLastCheckedAt(now);

    if (!result.ok && !wasAlreadyAvailable) {
      // SW nie jest dostępny (dev / iframe / brak wsparcia) — traktujemy jako
      // "sprawdzone, brak aktualizacji do zaproponowania".
      setUpdateStatus('uptodate');
      return;
    }

    // Krótkie okno na event `waiting` (workbox emituje asynchronicznie po
    // wykryciu nowego SW).
    await new Promise((resolve) => setTimeout(resolve, WAIT_FOR_WAITING_MS));

    if (updateEventFiredRef.current) {
      setUpdateStatus('available');
    } else {
      setUpdateStatus('uptodate');
    }
  }, []);

  const activate = useCallback(() => {
    activateWaitingServiceWorker();
  }, []);

  return { updateStatus, lastCheckedAt, checkForUpdates, activate };
}
